import api from './api';

class SourcingESignatureService {
  static async getESignatureRequests(params = {}) {
    try {
      const response = await api.get('/sourcing/e-signatures', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching RFP e-signature requests:', error);
      throw error;
    }
  }

  /**
   * Retrieve ALL e-signature requests linked to a given RFP, regardless of
   * signature status. Uses the dedicated audit endpoint so no status filter
   * or pagination limit is ever applied. Required for the RFP audit trail.
   */
  static async getESignaturesByRfpId(rfpNumber) {
    try {
      const response = await api.get(`/sourcing/e-signatures/rfp/${encodeURIComponent(rfpNumber)}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching e-signature requests by RFP ID:', error);
      throw error;
    }
  }

  /**
   * Upload (or replace) the signature image for an e-signature request.
   * @param {string} requestId  - The e-signature request ID (RFP-ESR-YYYY-NNNN)
   * @param {File}   file       - A File object from an <input type="file">
   */
  static async uploadSignatureImage(requestId, file) {
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await api.post(
        `/sourcing/e-signatures/${requestId}/upload-signature`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading signature image:', error);
      throw error;
    }
  }

  static async getOriginalContractDocument(rfpId) {
    try {
      const response = await api.get('/sourcing/e-signatures/contract-document', {
        params: { rfpId }
      });
      return response.data;
    } catch (error) {
      console.error('Error resolving original contract document:', error);
      throw error;
    }
  }

  /**
   * Upload the final signed document (only permitted when status is Completed).
   * @param {string} requestId  - The e-signature request ID
   * @param {File}   file       - A File object from an <input type="file">
   */
  static async uploadSignedDocument(requestId, file) {
    try {
      const formData = new FormData();
      formData.append('document', file);
      const response = await api.post(
        `/sourcing/e-signatures/${requestId}/upload-signed`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading signed document:', error);
      throw error;
    }
  }

  static async getESignatureRequestById(requestId) {
    try {
      const response = await api.get(`/sourcing/e-signatures/${requestId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching RFP e-signature request:', error);
      throw error;
    }
  }

  static async createESignatureRequest(requestData) {
    try {
      const response = await api.post('/sourcing/e-signatures', requestData);
      return response.data;
    } catch (error) {
      console.error('Error creating RFP e-signature request:', error);
      throw error;
    }
  }

  static async updateESignatureRequest(requestId, requestData) {
    try {
      const response = await api.put(`/sourcing/e-signatures/${requestId}`, requestData);
      return response.data;
    } catch (error) {
      console.error('Error updating RFP e-signature request:', error);
      throw error;
    }
  }

  static async deleteESignatureRequest(requestId) {
    try {
      const response = await api.delete(`/sourcing/e-signatures/${requestId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting RFP e-signature request:', error);
      throw error;
    }
  }

  static async sendESignatureRequest(requestId) {
    try {
      const response = await api.post(`/sourcing/e-signatures/${requestId}/send`);
      return response.data;
    } catch (error) {
      console.error('Error sending RFP e-signature request:', error);
      throw error;
    }
  }

  static async sendReminder(requestId, signerEmail = null) {
    try {
      const response = await api.post(`/sourcing/e-signatures/${requestId}/remind`, {
        signerEmail
      });
      return response.data;
    } catch (error) {
      console.error('Error sending reminder:', error);
      throw error;
    }
  }

  static async recordSignature(requestId, signerEmail, signatureData = {}) {
    try {
      const response = await api.post(`/sourcing/e-signatures/${requestId}/sign`, {
        signerEmail,
        signatureData
      });
      return response.data;
    } catch (error) {
      console.error('Error recording signature:', error);
      throw error;
    }
  }
}

export default SourcingESignatureService;
